import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ListTodo, Clock, AlertTriangle, CheckCircle2, Calendar,
  ChevronRight, Users, Target, LayoutList, Columns3,
  CalendarDays, Repeat, ChevronLeft, Plus, Trash2,
  Bookmark, Lock, MessageSquare, Send, Flag, UserPlus,
  Filter, Save, X, SquareCheck, Square, Zap
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths, getDay, isPast, isToday } from 'date-fns';
import { toast } from 'sonner';
import TaskTemplatesLibrary from './TaskTemplatesLibrary';
import TaskDetailDrawer from './TaskDetailDrawer';
import AutomationRules from './AutomationRules';
import { OrgBulkImportTasksDialog, OrgExportTasksDialog } from '@/components/tasks/BulkTasksDialogs';
import { Upload, Download } from 'lucide-react';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-green-100 text-green-800 border-green-200'
};

const SOURCE_BADGE = {
  client: { label: 'Client', color: 'bg-blue-100 text-blue-700', icon: Users },
  lead: { label: 'Lead', color: 'bg-purple-100 text-purple-700', icon: Target }
};

const GROUP_CONFIG = {
  overdue: { label: 'Overdue', icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
  dueToday: { label: 'Due Today', icon: Clock, color: 'text-amber-600', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
  dueThisWeek: { label: 'Due This Week', icon: Calendar, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  upcoming: { label: 'Upcoming', icon: ChevronRight, color: 'text-[#5D524D]', bgColor: 'bg-[#F5F3F0]', borderColor: 'border-[#E6E0D9]' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' }
};

const KANBAN_COLS = [
  { id: 'pending', label: 'Pending', color: 'border-amber-300', bg: 'bg-amber-50' },
  { id: 'in_progress', label: 'In Progress', color: 'border-blue-300', bg: 'bg-blue-50' },
  { id: 'completed', label: 'Completed', color: 'border-green-300', bg: 'bg-green-50' }
];

// ============ TASK CARD ============

function TaskCard({ task, onComplete, compact = false, selected = false, onSelect, showSelect = false, onClick }) {
  const sourceCfg = SOURCE_BADGE[task.source] || SOURCE_BADGE.client;
  const SourceIcon = sourceCfg.icon;
  const priorityCls = PRIORITY_COLORS[task.normalizedPriority] || PRIORITY_COLORS.medium;
  const isCompleted = task.normalizedStatus === 'completed';
  const dueDate = task.dueDate ? parseISO(task.dueDate) : null;
  const isOverdue = dueDate && isPast(dueDate) && !isCompleted;
  const subtasks = task.subtasks || task.checklist || [];
  const completedSubs = subtasks.filter(s => s.completed).length;
  const hasDeps = (task.dependencies || []).length > 0;

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-[#F5F3F0] transition-colors", isOverdue && "bg-red-50")} data-testid={`my-task-card-${task.id}`}>
        {!isCompleted ? (
          <button onClick={() => onComplete(task)} className="w-3.5 h-3.5 rounded-full border-2 border-[#D4CCC6] hover:border-green-500 shrink-0" data-testid={`complete-task-${task.id}`} />
        ) : (
          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
        )}
        <span className={cn("truncate flex-1", isCompleted && "line-through text-[#8C8078]")}>{task.title}</span>
        <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full", sourceCfg.color)}>{task.contextName?.split(' ')[0]}</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group bg-white rounded-lg border px-3 sm:px-4 py-3 hover:shadow-md transition-all cursor-pointer",
        isOverdue ? "border-red-200" : "border-[#E6E0D9]",
        isCompleted && "opacity-60",
        selected && "ring-2 ring-[#4A3B32]"
      )}
      data-testid={`my-task-card-${task.id}`}
      onClick={() => onClick?.(task)}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        {showSelect && (
          <button onClick={() => onSelect?.(task)} className="mt-0.5 shrink-0" data-testid={`select-task-${task.id}`}>
            {selected ? <SquareCheck className="w-5 h-5 text-[#4A3B32]" /> : <Square className="w-5 h-5 text-[#D4CCC6] hover:text-[#4A3B32]" />}
          </button>
        )}
        {!showSelect && !isCompleted && (
          <button onClick={(e) => { e.stopPropagation(); onComplete(task); }} className="mt-0.5 w-5 h-5 rounded-full border-2 border-[#D4CCC6] hover:border-green-500 hover:bg-green-50 transition-colors shrink-0" data-testid={`complete-task-${task.id}`} />
        )}
        {!showSelect && isCompleted && <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={cn("text-sm font-medium text-[#2C2420] leading-tight", isCompleted && "line-through text-[#8C8078]")}>{task.title}</p>
            {dueDate && (
              <span className={cn("text-[10px] whitespace-nowrap shrink-0 flex items-center gap-0.5", isOverdue ? "text-red-600 font-semibold" : "text-[#8C8078]")}>
                <Clock className="w-3 h-3" />
                {isToday(dueDate) ? 'Today' : format(dueDate, 'MMM d')}
              </span>
            )}
          </div>
          <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
            <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-0.5", sourceCfg.color)}>
              <SourceIcon className="w-2.5 h-2.5" />{task.contextName?.split(' ').slice(0,2).join(' ')}
            </span>
            <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full border font-medium", priorityCls)}>{task.normalizedPriority}</span>
            {task.category && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F5F3F0] text-[#5D524D] hidden sm:inline">{task.category}</span>}
            {(task.recurring || task.recurrencePattern) && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 inline-flex items-center gap-0.5">
                <Repeat className="w-2.5 h-2.5" />
                <span className="hidden sm:inline">{task.recurrencePattern || 'Recurring'}</span>
              </span>
            )}
            {hasDeps && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 inline-flex items-center gap-0.5 hidden sm:inline-flex">
                <Lock className="w-2.5 h-2.5" /><span className="hidden sm:inline">Depends</span>
              </span>
            )}
          </div>
          {task.assignedTo && (
            <span className="text-[10px] text-[#8C8078] flex items-center gap-1 mt-1"><Users className="w-2.5 h-2.5" />{task.assignedTo}</span>
          )}
          {subtasks.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1 bg-[#E6E0D9] rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${(completedSubs / subtasks.length) * 100}%` }} /></div>
              <span className="text-[10px] text-[#8C8078]">{completedSubs}/{subtasks.length}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ LIST VIEW ============

function ListView({ groups, showCompleted, onComplete, selectedTasks = [], onSelect, showSelect = false, onTaskClick }) {
  const groupOrder = ['overdue', 'dueToday', 'dueThisWeek', 'upcoming'];
  if (showCompleted) groupOrder.push('completed');

  const hasAnyTasks = groupOrder.some(k => (groups[k] || []).length > 0);

  if (!hasAnyTasks) {
    return (
      <div className="text-center py-16" data-testid="my-tasks-empty">
        <ListTodo className="w-12 h-12 text-[#D4CCC6] mx-auto mb-3" />
        <p className="text-[#5D524D] font-medium">No active tasks</p>
        <p className="text-sm text-[#8C8078] mt-1">Tasks from clients and leads will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-tasks-list-view">
      {groupOrder.map(groupKey => {
        const groupTasks = groups[groupKey] || [];
        if (groupTasks.length === 0) return null;
        const cfg = GROUP_CONFIG[groupKey];
        const GroupIcon = cfg.icon;
        return (
          <div key={groupKey} data-testid={`task-group-${groupKey}`}>
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className={cn("w-6 h-6 rounded-md flex items-center justify-center", cfg.bgColor)}>
                <GroupIcon className={cn("w-3.5 h-3.5", cfg.color)} />
              </div>
              <h3 className={cn("text-sm font-semibold", cfg.color)}>{cfg.label}</h3>
              <Badge variant="outline" className={cn("text-[10px]", cfg.borderColor)}>{groupTasks.length}</Badge>
            </div>
            <div className="space-y-2">
              {groupTasks.map(task => (
                <TaskCard key={task.id} task={task} onComplete={onComplete}
                  selected={selectedTasks.some(t => t.id === task.id)}
                  onSelect={onSelect} showSelect={showSelect}
                  onClick={onTaskClick}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============ KANBAN VIEW ============

function KanbanView({ tasks, onComplete, onDragEnd, onTaskClick }) {
  const grouped = { pending: [], in_progress: [], completed: [] };
  tasks.forEach(task => {
    const status = task.normalizedStatus || 'pending';
    if (grouped[status]) grouped[status].push(task);
    else grouped.pending.push(task);
  });

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="my-tasks-kanban-view">
        {KANBAN_COLS.map(col => (
          <Droppable key={col.id} droppableId={col.id}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={cn("min-w-[300px] flex-1 rounded-lg transition-colors", snapshot.isDraggingOver && "bg-[#F5F3F0]")}
                data-testid={`kanban-col-${col.id}`}
              >
                <div className={cn("rounded-t-lg px-3 py-2 border-t-4 mb-3", col.color, col.bg)}>
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-[#2C2420]">{col.label}</h4>
                    <Badge variant="outline" className="text-[10px]">{(grouped[col.id] || []).length}</Badge>
                  </div>
                </div>
                <div className="space-y-2 min-h-[200px]">
                  {(grouped[col.id] || []).map((task, index) => (
                    <Draggable key={task.id} draggableId={task.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={cn(snapshot.isDragging && "opacity-80 rotate-1")}
                        >
                          <TaskCard task={task} onComplete={onComplete} onClick={onTaskClick} />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {(grouped[col.id] || []).length === 0 && (
                    <div className="text-center py-8 text-xs text-[#B5AEA7] border-2 border-dashed border-[#E6E0D9] rounded-lg">
                      Drop tasks here
                    </div>
                  )}
                </div>
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
}

// ============ CALENDAR VIEW ============

function CalendarView({ tasks, onComplete, onTaskClick }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart); // 0=Sun

  const tasksByDate = {};
  tasks.forEach(task => {
    if (!task.dueDate) return;
    const key = format(parseISO(task.dueDate), 'yyyy-MM-dd');
    if (!tasksByDate[key]) tasksByDate[key] = [];
    tasksByDate[key].push(task);
  });

  return (
    <div data-testid="my-tasks-calendar-view">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 rounded-lg hover:bg-[#F5F3F0]">
          <ChevronLeft className="w-4 h-4 text-[#5D524D]" />
        </button>
        <h3 className="font-heading text-[#2C2420]">{format(currentMonth, 'MMMM yyyy')}</h3>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 rounded-lg hover:bg-[#F5F3F0]">
          <ChevronRight className="w-4 h-4 text-[#5D524D]" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="text-center text-[10px] font-medium text-[#8C8078] py-1">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for alignment */}
        {Array.from({ length: startDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="min-h-[80px]" />
        ))}

        {days.map(day => {
          const key = format(day, 'yyyy-MM-dd');
          const dayTasks = tasksByDate[key] || [];
          const today = isToday(day);

          return (
            <div
              key={key}
              className={cn(
                "min-h-[80px] rounded-lg border p-1 transition-colors",
                today ? "border-[#4A3B32] bg-[#4A3B32]/5" : "border-[#E6E0D9]",
                !isSameMonth(day, currentMonth) && "opacity-40"
              )}
            >
              <div className={cn("text-xs font-medium mb-1 px-1", today ? "text-[#4A3B32]" : "text-[#5D524D]")}>
                {format(day, 'd')}
              </div>
              <div className="space-y-0.5">
                {dayTasks.slice(0, 3).map(task => (
                  <TaskCard key={task.id} task={task} onComplete={onComplete} compact onClick={onTaskClick} />
                ))}
                {dayTasks.length > 3 && (
                  <div className="text-[10px] text-[#8C8078] px-2">+{dayTasks.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ BATCH TOOLBAR ============

function BatchToolbar({ selectedTasks, onBatchAction, onClearSelection }) {
  const count = selectedTasks.length;
  if (count === 0) return null;

  return (
    <div className="flex items-center gap-3 p-3 bg-[#4A3B32] rounded-lg mb-4 text-white" data-testid="batch-toolbar">
      <span className="text-sm font-medium">{count} selected</span>
      <div className="flex-1" />
      <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 text-xs" onClick={() => onBatchAction('complete')} data-testid="batch-complete">
        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Complete
      </Button>
      <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 text-xs" onClick={() => onBatchAction('priority')} data-testid="batch-priority">
        <Flag className="w-3.5 h-3.5 mr-1" />Priority
      </Button>
      <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 text-xs" onClick={() => onBatchAction('delete')} data-testid="batch-delete">
        <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
      </Button>
      <button onClick={onClearSelection} className="text-white/70 hover:text-white ml-2" data-testid="batch-clear"><X className="w-4 h-4" /></button>
    </div>
  );
}

// ============ SAVED FILTERS SIDEBAR ============

function SavedFiltersSidebar({ activeFilter, onFilterSelect, token }) {
  const [filters, setFilters] = useState({ systemViews: [], customFilters: [] });

  useEffect(() => {
    if (!token) return;
    api.getSavedFilters(token).then(res => setFilters(res.data)).catch(() => {});
  }, [token]);

  const ICONS = {
    'alert-triangle': AlertTriangle, 'clock': Clock, 'calendar': Calendar,
    'flag': Flag, 'users': Users, 'target': Target, 'repeat': Repeat,
    'lock': Lock, 'filter': Filter, 'bookmark': Bookmark
  };

  return (
    <div className="w-52 shrink-0 space-y-4" data-testid="saved-filters-sidebar">
      <div>
        <h4 className="text-[10px] font-semibold text-[#8C8078] uppercase tracking-wider mb-2 px-1">Smart Views</h4>
        <div className="space-y-0.5">
          {filters.systemViews.map(sv => {
            const Icon = ICONS[sv.icon] || Filter;
            return (
              <button
                key={sv.id}
                onClick={() => onFilterSelect(activeFilter === sv.id ? null : sv.id, sv.filters)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-all text-left",
                  activeFilter === sv.id ? "bg-[#4A3B32] text-white" : "text-[#5D524D] hover:bg-[#F5F3F0]"
                )}
                data-testid={`smart-view-${sv.id}`}
              >
                <Icon className="w-3.5 h-3.5" />{sv.name}
              </button>
            );
          })}
        </div>
      </div>
      {filters.customFilters.length > 0 && (
        <div>
          <h4 className="text-[10px] font-semibold text-[#8C8078] uppercase tracking-wider mb-2 px-1">Custom Filters</h4>
          <div className="space-y-0.5">
            {filters.customFilters.map(cf => (
              <button key={cf.id} onClick={() => onFilterSelect(activeFilter === cf.id ? null : cf.id, cf.filters)}
                className={cn("w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-all text-left",
                  activeFilter === cf.id ? "bg-[#4A3B32] text-white" : "text-[#5D524D] hover:bg-[#F5F3F0]"
                )}>
                <Bookmark className="w-3.5 h-3.5" />{cf.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ MAIN DASHBOARD ============

export default function MyTasksDashboard({ onQuickCapture }) {
  const { token } = useAuth();
  const [data, setData] = useState({ tasks: [], groups: {}, stats: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [view, setView] = useState('list');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [batchMode, setBatchMode] = useState(false);
  const [activeSmartFilter, setActiveSmartFilter] = useState(null);
  const [smartFilterCriteria, setSmartFilterCriteria] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAutomation, setShowAutomation] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showOrgBulkImport, setShowOrgBulkImport] = useState(false);
  const [showOrgExport, setShowOrgExport] = useState(false);

  const fetchTasks = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.source = filter;
      if (showCompleted || view === 'kanban') params.include_completed = 'true';
      const res = await api.getMyTasks(token, params);
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch tasks:', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchTasks(); }, [token, filter, showCompleted, view]);

  const handleComplete = async (task) => {
    try {
      const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
      if (task.source === 'lead') {
        await fetch(`${API_BASE}/leads/tasks/${task.id}/complete`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      } else {
        await fetch(`${API_BASE}/clients/${task.clientId}/workflow/${task.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status: 'Completed' })
        });
      }
      fetchTasks();
    } catch (e) { console.error('Failed to complete task:', e); }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const task = data.tasks.find(t => t.id === draggableId);
    if (!task) return;

    try {
      await api.updateTaskStatus(token, {
        taskId: task.id,
        source: task.source,
        newStatus: destination.droppableId,
        clientId: task.clientId
      });
      toast.success(`Moved to ${KANBAN_COLS.find(c => c.id === destination.droppableId)?.label}`);
      fetchTasks();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const toggleSelect = (task) => {
    setSelectedTasks(prev =>
      prev.find(t => t.id === task.id)
        ? prev.filter(t => t.id !== task.id)
        : [...prev, task]
    );
  };

  const handleBatchAction = async (action) => {
    if (selectedTasks.length === 0) return;
    let value = null;

    if (action === 'priority') {
      value = window.prompt('Set priority to (low, medium, high, urgent):', 'high');
      if (!value) return;
    }

    try {
      await api.batchUpdateTasks(token, {
        taskIds: selectedTasks.map(t => t.id),
        taskSources: selectedTasks.map(t => t.source),
        action,
        value
      });
      toast.success(`${selectedTasks.length} tasks updated`);
      setSelectedTasks([]);
      setBatchMode(false);
      fetchTasks();
    } catch (e) {
      toast.error('Batch operation failed');
    }
  };

  const handleSmartFilter = (id, criteria) => {
    setActiveSmartFilter(id);
    setSmartFilterCriteria(id ? criteria : null);
    if (criteria?.source) setFilter(criteria.source);
    else setFilter('all');
  };

  // Apply smart filter to tasks
  const getFilteredTasks = () => {
    if (!smartFilterCriteria) return data.tasks;
    return data.tasks.filter(task => {
      const c = smartFilterCriteria;
      if (c.overdue && !(task.dueDate && isPast(parseISO(task.dueDate)) && task.normalizedStatus !== 'completed')) return false;
      if (c.dueToday && !(task.dueDate && isToday(parseISO(task.dueDate)))) return false;
      if (c.priority && !c.priority.includes(task.normalizedPriority)) return false;
      if (c.recurring && !task.recurring) return false;
      if (c.blocked && !(task.dependencies?.length > 0)) return false;
      return true;
    });
  };

  const filteredTasks = getFilteredTasks();
  const { stats, groups } = data;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="my-tasks-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6" data-testid="my-tasks-header">
        <div>
          <h2 className="text-lg sm:text-xl font-heading text-[#2C2420]">
            {showTemplates ? 'Workflow Templates' : showAutomation ? 'Automation Rules' : 'My Tasks'}
          </h2>
          <p className="text-xs sm:text-sm text-[#5D524D]">
            {showTemplates ? 'Pre-built workflows that auto-create linked tasks'
              : showAutomation ? 'Auto-trigger workflows when leads change stage'
              : `${stats.active || 0} active tasks across all clients and leads`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant={showAutomation ? 'default' : 'outline'}
            onClick={() => { setShowAutomation(!showAutomation); setShowTemplates(false); }}
            className={cn("h-8 text-xs sm:text-sm sm:h-9", showAutomation ? 'bg-[#4A3B32] hover:bg-[#3A2E26]' : '')}
            data-testid="toggle-automation-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />{showAutomation ? 'Back' : 'Automation'}
          </Button>
          <Button
            size="sm"
            variant={showTemplates ? 'default' : 'outline'}
            onClick={() => { setShowTemplates(!showTemplates); setShowAutomation(false); }}
            className={cn("h-8 text-xs sm:text-sm sm:h-9", showTemplates ? 'bg-[#4A3B32] hover:bg-[#3A2E26]' : '')}
            data-testid="toggle-templates-btn"
          >
            <ListTodo className="w-3.5 h-3.5 mr-1.5" />{showTemplates ? 'Back' : 'Templates'}
          </Button>
          {!showTemplates && !showAutomation && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowOrgBulkImport(true)}
                className="h-8 text-xs sm:text-sm sm:h-9"
                data-testid="org-bulk-import-tasks-btn"
                title="Upload a CSV of tasks across multiple clients"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                <span className="hidden sm:inline">Bulk </span>Import
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowOrgExport(true)}
                className="h-8 text-xs sm:text-sm sm:h-9"
                data-testid="org-export-tasks-btn"
                title="Download all tasks across the org as CSV"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Export
              </Button>
              <Button size="sm" onClick={onQuickCapture} className="bg-[#4A3B32] hover:bg-[#3A2E26] h-8 text-xs sm:text-sm sm:h-9" data-testid="quick-capture-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" />Capture
              </Button>
            </>
          )}
        </div>
      </div>

      {showTemplates && <TaskTemplatesLibrary />}
      {showAutomation && <AutomationRules />}

      {!showTemplates && !showAutomation && <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-6" data-testid="my-tasks-stats">
        <div className="bg-white rounded-lg border border-[#E6E0D9] p-3" data-testid="stat-total-tasks">
          <div className="flex items-center gap-2 mb-1"><ListTodo className="w-4 h-4 text-[#4A3B32]" /><span className="text-xs text-[#8C8078]">Total Active</span></div>
          <p className="text-2xl font-heading text-[#2C2420]">{stats.active || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-red-200 p-3" data-testid="stat-overdue-tasks">
          <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-red-600" /><span className="text-xs text-red-600">Overdue</span></div>
          <p className="text-2xl font-heading text-red-600">{stats.overdue || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-amber-200 p-3" data-testid="stat-due-today">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-amber-600" /><span className="text-xs text-amber-600">Due Today</span></div>
          <p className="text-2xl font-heading text-amber-600">{stats.dueToday || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-blue-200 p-3" data-testid="stat-due-week">
          <div className="flex items-center gap-2 mb-1"><Calendar className="w-4 h-4 text-blue-600" /><span className="text-xs text-blue-600">This Week</span></div>
          <p className="text-2xl font-heading text-blue-600">{stats.dueThisWeek || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-green-200 p-3" data-testid="stat-upcoming-tasks">
          <div className="flex items-center gap-2 mb-1"><ChevronRight className="w-4 h-4 text-green-600" /><span className="text-xs text-green-600">Upcoming</span></div>
          <p className="text-2xl font-heading text-green-600">{stats.upcoming || 0}</p>
        </div>
      </div>

      {/* Quick Filter Chips — one-click access to the most-used system views */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="quick-filter-chips">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#8C8078] mr-1">Quick view:</span>
        {[
          { id: 'sv-overdue',  label: 'Overdue',       icon: AlertTriangle, count: stats.overdue || 0, criteria: { overdue: true } },
          { id: 'sv-today',    label: 'Due today',     icon: Clock,         count: stats.dueToday || 0, criteria: { dueToday: true } },
          { id: 'sv-week',     label: 'This week',     icon: Calendar,      count: stats.dueThisWeek || 0, criteria: {} },
          { id: 'sv-high',     label: 'High priority', icon: Flag,          count: null, criteria: { priority: ['high', 'urgent'] } },
          { id: 'sv-recurring',label: 'Recurring',     icon: Repeat,        count: null, criteria: { recurring: true } },
        ].map(chip => {
          const active = activeSmartFilter === chip.id;
          const Icon = chip.icon;
          return (
            <button
              key={chip.id}
              onClick={() => handleSmartFilter(active ? null : chip.id, active ? null : chip.criteria)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                active
                  ? "bg-[#4A3B32] text-white border-[#4A3B32]"
                  : "bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#A86A3A] hover:text-[#A86A3A]"
              )}
              data-testid={`quick-chip-${chip.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {chip.label}
              {chip.count !== null && chip.count > 0 && (
                <span className={cn(
                  "ml-0.5 px-1.5 rounded-full text-[10px]",
                  active ? "bg-white/20" : "bg-[#F5F3F0] text-[#8C8078]"
                )}>{chip.count}</span>
              )}
            </button>
          );
        })}
        {activeSmartFilter && (
          <button
            onClick={() => handleSmartFilter(null, null)}
            className="text-xs text-[#8C8078] hover:text-[#2C2420] underline ml-1"
            data-testid="quick-chip-clear"
          >
            Clear
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap" data-testid="my-tasks-filters">
          <div className="flex items-center gap-1 bg-[#F5F3F0] rounded-lg p-1">
            {[
              { id: 'all', label: 'All' },
              { id: 'client', label: 'Clients', icon: Users },
              { id: 'lead', label: 'Leads', icon: Target }
            ].map(f => (
              <button key={f.id} onClick={() => { setFilter(f.id); setActiveSmartFilter(null); setSmartFilterCriteria(null); }}
                className={cn("px-2.5 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1",
                  filter === f.id && !activeSmartFilter ? "bg-white text-[#2C2420] shadow-sm" : "text-[#5D524D] hover:text-[#2C2420]"
                )} data-testid={`filter-${f.id}`}
              >
                {f.icon && <f.icon className="w-3.5 h-3.5" />}{f.label}
              </button>
            ))}
          </div>

          {view === 'list' && (
            <>
              <button onClick={() => setShowCompleted(!showCompleted)}
                className={cn("px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1",
                  showCompleted ? "bg-green-50 text-green-700 border-green-200" : "bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#4A3B32]"
                )} data-testid="toggle-completed"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /><span className="hidden sm:inline">{showCompleted ? 'Hide' : 'Show'}</span> Done
              </button>
              <button onClick={() => { setBatchMode(!batchMode); setSelectedTasks([]); }}
                className={cn("px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1",
                  batchMode ? "bg-[#4A3B32] text-white border-[#4A3B32]" : "bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#4A3B32]"
                )} data-testid="toggle-batch-mode"
              >
                <SquareCheck className="w-3.5 h-3.5" />Select
              </button>
            </>
          )}
        </div>

        {/* View Switcher */}
        <div className="flex items-center gap-1 bg-[#F5F3F0] rounded-lg p-1" data-testid="view-switcher">
          {[
            { id: 'list', label: 'List', icon: LayoutList },
            { id: 'kanban', label: 'Board', icon: Columns3 },
            { id: 'calendar', label: 'Calendar', icon: CalendarDays }
          ].map(v => (
            <button key={v.id} onClick={() => { setView(v.id); setBatchMode(false); setSelectedTasks([]); }}
              className={cn("px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5",
                view === v.id ? "bg-white text-[#2C2420] shadow-sm" : "text-[#5D524D] hover:text-[#2C2420]"
              )} data-testid={`view-${v.id}`}
            >
              <v.icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{v.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Batch Toolbar */}
      <BatchToolbar selectedTasks={selectedTasks} onBatchAction={handleBatchAction} onClearSelection={() => { setSelectedTasks([]); setBatchMode(false); }} />

      {/* Main Content with Sidebar */}
      <div className="flex gap-6">
        {/* Saved Filters Sidebar - hidden on mobile */}
        <div className="hidden md:block">
          <SavedFiltersSidebar activeFilter={activeSmartFilter} onFilterSelect={handleSmartFilter} token={token} />
        </div>

        {/* View Content */}
        <div className="flex-1 min-w-0" data-testid="my-tasks-groups">
          {view === 'list' && <ListView groups={groups} showCompleted={showCompleted} onComplete={handleComplete} selectedTasks={selectedTasks} onSelect={toggleSelect} showSelect={batchMode} onTaskClick={setSelectedTask} />}
          {view === 'kanban' && <KanbanView tasks={filteredTasks} onComplete={handleComplete} onDragEnd={handleDragEnd} onTaskClick={setSelectedTask} />}
          {view === 'calendar' && <CalendarView tasks={filteredTasks} onComplete={handleComplete} onTaskClick={setSelectedTask} />}
        </div>
      </div>
      </>}

      {/* Task Detail Drawer */}
      <TaskDetailDrawer
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        onUpdate={() => { fetchTasks(); }}
      />

      {/* Org-wide Bulk Import & Export Dialogs */}
      <OrgBulkImportTasksDialog
        open={showOrgBulkImport}
        onOpenChange={setShowOrgBulkImport}
        onImported={() => fetchTasks()}
      />
      <OrgExportTasksDialog
        open={showOrgExport}
        onOpenChange={setShowOrgExport}
      />
    </div>
  );
}
